import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { ArrowRight, ArrowLeft, X } from 'lucide-react';

// ---------------------------------------------------------------------------
// Project data
// media types:
//   sprite  – horizontal sprite sheet animated with CSS steps()
//   gallery – list of images (first one is the cover)
//   pair    – input → output images shown side by side
//   flow    – illustrated pipeline (used where no publishable imagery exists)
// ---------------------------------------------------------------------------

const featured = {
  id: 'xision',
  category: 'vision',
  kicker: 'Computer Vision · 3D Avatars',
  title: 'XISION',
  subtitle: 'Photorealistic, animatable 3D avatars on iPhone',
  summary:
    'Full-body digital humans from a single consumer capture, rendered in real time on mobile. The pipeline combines 3D Gaussian Splatting with a fitted SMPL-X body model and a Metal-based renderer.',
  pipeline: [
    { step: 'Capture & isolate', detail: 'Remove background and normalize the 3D Gaussian cloud.' },
    { step: 'Fit & correct', detail: 'Fit SMPL-X pose and shape; correct orientation and lean.' },
    { step: 'Bind & animate', detail: 'Attach splats to mesh triangles and bake walk / turn clips.' },
    { step: 'Validate & deploy', detail: 'Check visual and geometric quality; render on iOS via Metal.' }
  ],
  media: { type: 'sprite', src: '/projects/xision-walk.jpg', frames: 5, alt: 'Gaussian-splat avatar walking sequence' },
  detail: {
    overview:
      'XISION develops photorealistic, animatable full-body digital avatars from a consumer capture for real-time visualization on iPhone. Splats are locked to the surface of a fitted SMPL-X mesh so the avatar can walk and turn while keeping its captured appearance.',
    highlights: [
      'Corrected inverted / head-down capture orientation; restored coherent full-body walking and preserved facial splats.',
      'Built a surface-locked animation representation with per-splat triangle coordinates, local offsets and covariance.',
      'Added capture-quality checks for coverage, missing limbs, scale and scan noise; aligned output with the backend API schema.',
      'Validated offline walk / turntable outputs and isolated a mobile-specific color artifact from asset-generation errors.'
    ],
    next:
      'Improving shoulder / arm fitting and binding accuracy, removing intermittent foot artifacts, fixing depth sorting and alpha blending in the Metal renderer, and pushing photorealism while keeping interactive mobile performance.',
    stack: ['3D Gaussian Splatting', 'SMPL-X', 'PyTorch', 'Geometry Processing', 'Metal', 'iOS']
  }
};

const projects = [
  {
    id: 'mockberg',
    category: 'vision',
    kicker: 'Computer Vision · Retail',
    title: 'Mockberg Virtual Try-On',
    summary: 'Studio-grade jewelry imagery without a studio: products placed on generated models with natural pose, styling and light.',
    tags: ['Product Replacement', 'Pose Estimation', 'Generative Models'],
    media: {
      type: 'gallery',
      images: [
        { src: '/projects/mockberg-1.jpg', alt: 'Model wearing a gold Mockberg watch, yellow top' },
        { src: '/projects/mockberg-5.jpg', alt: 'Model with gold watch and bracelet, pink top' },
        { src: '/projects/mockberg-3.jpg', alt: 'Model wearing a gold-strap watch' },
        { src: '/projects/mockberg-4.jpg', alt: 'Same pose with the watch replaced by a black-strap model' },
        { src: '/projects/mockberg-2.jpg', alt: 'Close-up portrait with gold watch' }
      ]
    },
    detail: {
      overview:
        'A complete virtual try-on workflow for jewelry. Real products are inserted into generated photography with their details intact, while models, styling and backgrounds are tuned to the brand.',
      highlights: [
        'Product replacement — pieces are inserted with precise placement and faithful material detail.',
        'Style reference — outfits, accessories and palette chosen to complement each product.',
        'Pose estimation — natural hand and body positions that keep the piece clearly visible.',
        'Background removal / addition — scenes rebuilt to match the brand’s visual language.',
        'Photo model generation — realistic faces, proportions and clothing for every look.',
        'Virtual try-on — customers preview how a piece would look when worn.'
      ],
      stack: ['Product Replacement', 'Style Reference', 'Pose Estimation', 'Background Removal', 'Photo Model Generation']
    }
  },
  {
    id: 'dobbli3d',
    category: 'vision',
    kicker: 'Computer Vision · 3D Tours',
    title: 'Dobbli3D Sparse Panoramas',
    summary: 'High-quality 360° panoramas from a handful of photos with minimal overlap — the hard case classic stitchers fail on.',
    tags: ['SuperPoint', 'SIFT', 'GPU Stitching'],
    media: { type: 'gallery', images: [{ src: '/projects/dobbli-panorama.jpg', alt: 'Interior stairwell panorama stitched from sparse images' }] },
    detail: {
      overview:
        'A stitching pipeline built for sparse capture. Learned and classical feature detection align images that share very little overlap, so immersive virtual tours can be built from far fewer photos than traditional methods require.',
      highlights: [
        'Hybrid feature matching with SuperPoint and SIFT for robust alignment under low overlap.',
        'Custom horizontal and vertical stitching to form a seamless spherical panorama.',
        'Pre-processing for exposure normalization and contrast enhancement across shots.',
        'Post-processing with inpainting and sharpening for a polished result.',
        'GPU-accelerated end to end for large property datasets.'
      ],
      stack: ['SuperPoint', 'SIFT', 'Custom Stitching', 'Inpainting', 'GPU Acceleration']
    }
  },
  {
    id: 'floorplan',
    category: 'vision',
    kicker: 'Computer Vision · Architecture',
    title: 'Floorplan to 3D',
    summary: 'From a flat plan to a furnished 3D render, plus facade extrusion and full exterior remodelling.',
    tags: ['Floorplan Extrusion', 'Render Generation', 'Facade Redesign'],
    media: {
      type: 'pair',
      before: { src: '/projects/floorplan-2d.png', alt: '2D floor plan input', label: 'Plan' },
      after: { src: '/projects/floorplan-3d.jpg', alt: 'Furnished 3D floor plan render', label: 'Render' }
    },
    gallery: [
      { src: '/projects/floorplan-3d.jpg', alt: 'Furnished 3D floor plan render' },
      { src: '/projects/extrusion-render.jpg', alt: 'Generated exterior render of a two-storey house' },
      { src: '/projects/extrusion-blender.jpg', alt: 'Extruded building model and generated render in the modelling tool' }
    ],
    detail: {
      overview:
        'Interior and exterior redesign for residential spaces: floor plan alterations, house render generation and facade remodelling. Plans are extruded into 3D geometry, furnished, and rendered; exteriors are re-imagined with new materials, landscaping and context.',
      highlights: [
        '2D plan → extruded 3D model → furnished, lit render.',
        'Interior layouts redesigned for function, comfort and modern aesthetics.',
        'Facade remodelling and exterior renders generated from simple massing models.',
        'Landscaping and urban context so the result sits naturally in its surroundings.'
      ],
      stack: ['Floorplan Extrusion', '3D Modelling', 'Render Generation', 'Facade Remodelling', 'Landscaping']
    }
  },
  {
    id: 'virtual-fit',
    category: 'vision',
    kicker: 'Computer Vision · Fashion',
    title: 'Virtual Fit',
    summary: 'Clothing parsing for try-on: portraits segmented into upper, lower and full-body garments with clean alpha mattes.',
    tags: ['U²-Net', 'StyleGAN', 'Cloth Parsing'],
    media: {
      type: 'flow',
      steps: ['Portrait', 'U²-Net parsing', 'Garment masks', 'Alpha mattes'],
      chips: [
        { label: 'Upper', color: '#c0504d' },
        { label: 'Lower', color: '#6a9a5b' },
        { label: 'Full', color: '#d4b13f' }
      ]
    },
    detail: {
      overview:
        'Cloth parsing built on a pre-trained U²-Net for human portrait segmentation. Clothing is split into three classes — upper body, lower body and full body — and StyleGAN is used for realistic garment generation on top of the segmentation.',
      highlights: [
        'Three-class garment segmentation: upper (red), lower (green) and full body (yellow).',
        'Per-class alpha images for clean compositing into downstream apps.',
        'StyleGAN-based generation for realistic garment textures.',
        'Drop-in component for virtual try-on, personalized styling and fashion analytics.'
      ],
      stack: ['U²-Net', 'StyleGAN', 'PyTorch', 'Segmentation', 'Alpha Matting']
    }
  },
  {
    id: 'nailsent',
    category: 'vision',
    kicker: 'Computer Vision · Beauty',
    title: 'NailSent',
    summary: 'Virtual nail polish: precise nail segmentation followed by GAN-generated colors, gradients and patterns.',
    tags: ['DeepLabV3+', 'MobileNetV2', 'StyleGAN2 / CycleGAN'],
    media: {
      type: 'flow',
      steps: ['Hand photo', 'DeepLabV3+ masks', 'GAN styling', 'Try-on'],
      chips: [
        { label: 'Solid', color: '#8fbf6a' },
        { label: 'Gradient', color: '#c98aa8' },
        { label: 'Pattern', color: '#7a8fc4' }
      ]
    },
    detail: {
      overview:
        'A two-stage virtual nail try-on. Nails are segmented from complex backgrounds, then a GAN renders photorealistic polish that follows each nail’s shape and surface.',
      highlights: [
        'Nail segmentation with DeepLabV3+ on a MobileNetV2 backbone — atrous convolution and encoder–decoder design for fine detail at any resolution.',
        'Polish styling with StyleGAN2 or CycleGAN for solid colors, gradients and patterns.'
      ],
      stack: ['DeepLabV3+', 'MobileNetV2', 'StyleGAN2', 'CycleGAN', 'PyTorch']
    }
  },
  {
    id: 'kisaandost',
    category: 'language',
    kicker: 'LLM · AgriTech',
    title: 'KisaanDost AI',
    summary: 'An Urdu, voice-first assistant that answers farmers’ questions with a Bloomz model fine-tuned on local agricultural data.',
    tags: ['Bloomz Fine-tuning', 'Urdu Voice', 'Intent & Entities'],
    media: { type: 'flow', steps: ['Urdu voice', 'Intent + entities', 'Fine-tuned Bloomz', 'Spoken answer'] },
    detail: {
      overview:
        'A mobile-first web and Android assistant that helps farmers through voice conversations in their own language. A Python chatbot classifies intent and extracts entities, then a fine-tuned LLM answers from an expert-backed agricultural dataset.',
      highlights: [
        'Bloomz LLM fine-tuned on local agricultural data sources.',
        'Urdu voice integration for natural, hands-free use.',
        'Location API integration for weather-aware advice.',
        'Personalized farmer profiles to track individual needs.',
        'Dataset fact-checked and contributed by industry professionals.',
        'Accessible UI/UX designed for farmers.'
      ],
      stack: ['Python', 'PyTorch', 'Bloomz', 'Flask', 'JavaScript', 'Figma'],
      credit: 'Built at FAST-NUCES Islamabad under Dr. Mirza Omer Beg. LLM fine-tuning and dataset curation led by Syed Muhammad Asad.'
    }
  },
  {
    id: 'real-estate',
    category: 'language',
    kicker: 'AI Automation · Real Estate',
    title: 'Real Estate Lead Engine',
    summary: 'End-to-end lead generation and qualification with AI screening, CRM sync and personalized property recommendations.',
    tags: ['Lead Scoring', 'CRM Automation', 'Zapier'],
    media: { type: 'flow', steps: ['Capture', 'AI qualify', 'Engage', 'Close'] },
    detail: {
      overview:
        'A complete workflow for generating and managing real estate leads — capture, qualification, assignment, engagement and proposals — with AI automating screening and personalization across multiple projects.',
      highlights: [
        'Lead sources across website, social and Google Ads plus offline campaigns.',
        'Automatic capture from Facebook and Google Ads into the CRM with real-time API sync.',
        'AI screening for lead quality and priority, followed by agent verification.',
        'AI-driven property recommendations for personalized engagement.',
        'Proposal and approval workflow tracking every stage to close.'
      ],
      stack: ['Machine Learning', 'CRM', 'Facebook Ads', 'Google Ads', 'Zapier'],
      list: {
        label: 'Deployed across',
        items: ['Mall of Imarat', 'Babylon', 'Cybertower', 'Imarat Residencies 1 & 2', 'Golf Floras', 'Hoon Farmhouses']
      }
    }
  },
  {
    id: 'rasa-insurance',
    category: 'language',
    kicker: 'Conversational AI · Insurance',
    title: 'Insurance Assistant',
    summary: 'A Rasa + Django assistant that answers policy questions, files claims and generates quotes in real time.',
    tags: ['Rasa NLU', 'Django', 'WebSockets'],
    media: { type: 'flow', steps: ['inquire_policy', 'file_claim', 'get_quote', 'handoff'], mono: true },
    detail: {
      overview:
        'Rasa integrated with Django to give an insurance company a conversational front door. The assistant reads live policy data, submits claims and keeps context across sessions.',
      highlights: [
        'Rasa NLU for intent classification and entity recognition (policy_number, insurance_type), with training data generated from the database.',
        'Django models (PolicyInformation, InsuranceType, Message) for policies, claims, contractors and conversation history.',
        'WebSocket channel between the Django frontend and Rasa for instant messaging.',
        'Custom API endpoints — get_policy_info, submit_claim, get_contractor_info — called from Rasa custom actions.',
        'Authenticated sessions, CRM integration and a robust fallback policy.'
      ],
      stack: ['Rasa', 'Django', 'WebSockets', 'REST APIs', 'CRM Integration']
    }
  },
  {
    id: 'rag-architecture',
    category: 'language',
    kicker: 'RAG · Architecture',
    title: 'Architecture Q&A',
    summary: 'A retrieval-augmented system trained on LLaMA-generated synthetic Q&A for accurate answers to architecture questions.',
    tags: ['RAG', 'LLaMA Synthetic Data', 'Hugging Face'],
    media: { type: 'flow', steps: ['Question', 'Retrieve Q/A', 'Generate', 'Grounded answer'] },
    detail: {
      overview:
        'A domain-specific RAG system for architecture. Synthetic question–answer pairs generated with LLaMA form the retrieval corpus; relevant pairs are fetched before generation so answers stay accurate and contextual.',
      highlights: [
        'Synthetic Q/A corpus generated with LLaMA and structured with custom preprocessing.',
        'Retriever selects the most relevant pairs to ground each answer.',
        'Retrieval and generation trained together and evaluated with dedicated test scripts.',
        'Model weights and configuration published on Hugging Face for reuse and fine-tuning.'
      ],
      stack: ['LLaMA', 'Transformers', 'Hugging Face', 'Python', 'RAG']
    }
  }
];

const filters = [
  { id: 'all', label: 'All work' },
  { id: 'vision', label: 'Computer Vision' },
  { id: 'language', label: 'Language & LLM' }
];

// ---------------------------------------------------------------------------
// Media renderers
// ---------------------------------------------------------------------------

const ink = 'text-[#2c2416] dark:text-[#e8e6e3]';
const hairline = 'border-[#2c2416]/10 dark:border-[#e8e6e3]/10';

function WalkSprite({ src, frames, alt, className = '' }) {
  return (
    <div
      role="img"
      aria-label={alt}
      className={`kt-walk ${className}`}
      style={{
        backgroundImage: `url("${src}")`,
        backgroundSize: `${frames * 100}% 100%`,
        animationTimingFunction: `steps(${frames})`
      }}
    />
  );
}

function FlowPanel({ steps, chips, mono, large }) {
  return (
    <div className="kt-grid-bg absolute inset-0 flex flex-col justify-center px-6 sm:px-8">
      <div className="space-y-2.5">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="text-[10px] tracking-widest opacity-40 w-5 shrink-0">{String(i + 1).padStart(2, '0')}</span>
            <div
              className={`flex-1 rounded-lg border ${hairline} bg-[#f7f4ed]/80 dark:bg-[#0f0f0f]/80 px-3 ${large ? 'py-3 text-[14px]' : 'py-2 text-[12px]'} ${mono ? 'font-mono' : 'font-light tracking-wide'} transition-transform duration-500`}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              {s}
            </div>
          </div>
        ))}
      </div>
      {chips && (
        <div className="flex gap-2 mt-5 pl-8">
          {chips.map((c) => (
            <span key={c.label} className="flex items-center gap-1.5 text-[10px] tracking-widest uppercase opacity-70">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} />
              {c.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function CardMedia({ project }) {
  const m = project.media;
  if (m.type === 'gallery') {
    return (
      <img
        src={m.images[0].src}
        alt={m.images[0].alt}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
      />
    );
  }
  if (m.type === 'pair') {
    return (
      <div className="absolute inset-0 grid grid-cols-2 bg-white">
        <div className="relative flex items-center justify-center p-4 border-r border-[#2c2416]/10">
          <img src={m.before.src} alt={m.before.alt} loading="lazy" className="max-h-full max-w-full object-contain" />
          <span className="absolute bottom-3 left-3 text-[9px] tracking-widest uppercase text-[#2c2416]/60">{m.before.label}</span>
        </div>
        <div className="relative overflow-hidden">
          <img
            src={m.after.src}
            alt={m.after.alt}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
          />
          <span className="absolute bottom-3 left-3 text-[9px] tracking-widest uppercase text-[#2c2416]/60">{m.after.label}</span>
        </div>
      </div>
    );
  }
  return <FlowPanel steps={m.steps} chips={m.chips} mono={m.mono} />;
}

// ---------------------------------------------------------------------------
// Detail modal
// ---------------------------------------------------------------------------

function ProjectModal({ project, onClose, onPrev, onNext }) {
  const [active, setActive] = useState(0);
  const closeRef = useRef(null);

  useEffect(() => {
    setActive(0);
  }, [project.id]);

  const handlers = useRef({ onClose, onPrev, onNext });
  handlers.current = { onClose, onPrev, onNext };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') handlers.current.onClose();
      if (e.key === 'ArrowRight') handlers.current.onNext();
      if (e.key === 'ArrowLeft') handlers.current.onPrev();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.__lenis?.stop();
    closeRef.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      window.__lenis?.start();
    };
  }, []);

  const images =
    project.gallery ||
    (project.media.type === 'gallery' ? project.media.images : null);
  const d = project.detail;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm animate-[fadeIn_0.3s_ease-out]"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="kt-project-title"
    >
      <div
        data-lenis-prevent
        className={`relative w-full sm:max-w-[960px] max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-[#f7f4ed] dark:bg-[#141414] ${ink} border ${hairline} shadow-2xl animate-[fadeInUp_0.4s_ease-out]`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`sticky top-0 z-10 flex items-center justify-between px-6 sm:px-10 py-4 bg-[#f7f4ed]/90 dark:bg-[#141414]/90 backdrop-blur border-b ${hairline}`}>
          <p className="text-[10px] tracking-widest uppercase opacity-60">{project.kicker}</p>
          <div className="flex items-center gap-1">
            <button onClick={onPrev} aria-label="Previous project" className="p-2 rounded-full hover:bg-[#2c2416]/5 dark:hover:bg-[#e8e6e3]/5 transition">
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button onClick={onNext} aria-label="Next project" className="p-2 rounded-full hover:bg-[#2c2416]/5 dark:hover:bg-[#e8e6e3]/5 transition">
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button ref={closeRef} onClick={onClose} aria-label="Close" className="ml-2 p-2 rounded-full border border-[#2c2416]/10 dark:border-[#e8e6e3]/10 hover:bg-[#2c2416]/5 dark:hover:bg-[#e8e6e3]/5 transition">
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        <div className="px-6 sm:px-10 pt-8 pb-10">
          <h3 id="kt-project-title" className="text-[clamp(1.75rem,4vw,2.75rem)] font-extralight tracking-tight leading-tight mb-3">
            {project.title}
          </h3>
          <p className="text-[15px] font-light opacity-80 max-w-[680px] leading-relaxed mb-8">{project.summary}</p>

          {/* Media */}
          {project.media.type === 'sprite' && (
            <div className={`rounded-2xl border ${hairline} bg-white flex items-center justify-center py-6 mb-10`}>
              <WalkSprite {...project.media} className="h-[260px] sm:h-[320px] aspect-[220/409]" />
            </div>
          )}
          {images && (
            <div className="mb-10">
              <div className={`relative rounded-2xl overflow-hidden border ${hairline} bg-[#2c2416]/[0.03] dark:bg-[#e8e6e3]/[0.03]`}>
                <img
                  key={images[active].src}
                  src={images[active].src}
                  alt={images[active].alt}
                  className="w-full max-h-[60vh] object-contain mx-auto animate-[fadeIn_0.4s_ease-out]"
                />
              </div>
              {images.length > 1 && (
                <div className="flex gap-3 mt-4 overflow-x-auto pb-1">
                  {images.map((img, i) => (
                    <button
                      key={img.src}
                      onClick={() => setActive(i)}
                      aria-label={`Show image ${i + 1}`}
                      className={`shrink-0 w-20 h-20 rounded-xl overflow-hidden border transition-all duration-300 ${i === active ? 'border-[#2c2416]/60 dark:border-[#e8e6e3]/60 opacity-100' : `${hairline} opacity-50 hover:opacity-90`}`}
                    >
                      <img src={img.src} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {project.media.type === 'flow' && (
            <div className={`relative rounded-2xl overflow-hidden border ${hairline} h-[300px] mb-10 bg-[#2c2416]/[0.02] dark:bg-[#e8e6e3]/[0.02]`}>
              <FlowPanel {...project.media} large />
            </div>
          )}

          {/* Body */}
          <div className="grid md:grid-cols-[1.1fr_1fr] gap-10">
            <div>
              <p className="text-[11px] tracking-widest uppercase opacity-60 mb-3">Overview</p>
              <p className="text-[14px] leading-relaxed font-light opacity-85 mb-8">{d.overview}</p>

              {project.pipeline && (
                <>
                  <p className="text-[11px] tracking-widest uppercase opacity-60 mb-3">Pipeline</p>
                  <ol className="space-y-3 mb-8">
                    {project.pipeline.map((p, i) => (
                      <li key={i} className={`border-l ${hairline} pl-4`}>
                        <p className="text-[13px] font-medium">{String(i + 1).padStart(2, '0')} · {p.step}</p>
                        <p className="text-[13px] font-light opacity-75">{p.detail}</p>
                      </li>
                    ))}
                  </ol>
                </>
              )}

              {d.next && (
                <>
                  <p className="text-[11px] tracking-widest uppercase opacity-60 mb-3">In progress</p>
                  <p className="text-[14px] leading-relaxed font-light opacity-85 mb-8">{d.next}</p>
                </>
              )}

              {d.list && (
                <>
                  <p className="text-[11px] tracking-widest uppercase opacity-60 mb-3">{d.list.label}</p>
                  <p className="text-[14px] font-light opacity-85 mb-8">{d.list.items.join(' · ')}</p>
                </>
              )}

              {d.credit && <p className="text-[12px] font-light opacity-60 italic">{d.credit}</p>}
            </div>

            <div>
              <p className="text-[11px] tracking-widest uppercase opacity-60 mb-3">
                {project.pipeline ? 'Engineering contributions' : 'Highlights'}
              </p>
              <ul className="space-y-3 mb-8">
                {d.highlights.map((h, i) => (
                  <li key={i} className="flex gap-3 text-[13px] leading-relaxed font-light opacity-85">
                    <span className="mt-[9px] w-1 h-1 rounded-full bg-current opacity-60 shrink-0" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] tracking-widest uppercase opacity-60 mb-3">Stack</p>
              <div className="flex flex-wrap gap-2">
                {d.stack.map((t) => (
                  <span key={t} className="text-[10px] px-3 py-1 border border-[#2c2416]/20 dark:border-[#e8e6e3]/20 rounded-full tracking-widest font-light uppercase opacity-80">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

export default function ProjectPortfolio() {
  const [filter, setFilter] = useState('all');
  const [openId, setOpenId] = useState(null);
  const [shown, setShown] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          obs.disconnect();
        }
      },
      { threshold: 0.05 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const showFeatured = filter === 'all' || filter === featured.category;
  const visible = useMemo(() => projects.filter((p) => filter === 'all' || p.category === filter), [filter]);

  // Modal navigation follows the current filter (featured project first).
  const sequence = useMemo(() => [...(showFeatured ? [featured] : []), ...visible], [showFeatured, visible]);
  const openIdx = sequence.findIndex((p) => p.id === openId);
  const openProject = openIdx >= 0 ? sequence[openIdx] : null;

  const close = useCallback(() => setOpenId(null), []);
  const step = useCallback(
    (dir) => {
      if (openIdx < 0) return;
      const n = sequence.length;
      setOpenId(sequence[(openIdx + dir + n) % n].id);
    },
    [openIdx, sequence]
  );
  const prev = useCallback(() => step(-1), [step]);
  const next = useCallback(() => step(1), [step]);

  const reveal = (delay = 0) => ({
    className: `transition-all duration-1000 ${shown ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`,
    style: { transitionDelay: `${delay}ms` }
  });

  return (
    <section
      id="portfolio"
      ref={sectionRef}
      className="py-32 px-8 lg:px-12 border-t border-[#2c2416]/5 dark:border-[#e8e6e3]/5"
    >
      <div className="max-w-[1200px] mx-auto">
        {/* Header */}
        <div {...reveal(0)}>
          <div className="mb-16 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
            <div>
              <p className="text-[11px] tracking-widest uppercase opacity-60 mb-4">PORTFOLIO</p>
              <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-extralight leading-tight tracking-tight mb-6">
                Vision & language systems
              </h2>
              <p className="text-[15px] text-[#2c2416]/90 dark:text-[#e8e6e3]/85 font-light max-w-[600px]">
                From photorealistic avatars to Urdu voice assistants — selected research and product work across computer vision and LLMs.
              </p>
            </div>
            <div className={`inline-flex self-start lg:self-auto rounded-full border ${hairline} p-1`} role="tablist" aria-label="Filter projects">
              {filters.map((f) => (
                <button
                  key={f.id}
                  role="tab"
                  aria-selected={filter === f.id}
                  onClick={() => setFilter(f.id)}
                  className={`px-4 py-2 rounded-full text-[11px] tracking-widest uppercase transition-all duration-500 ${
                    filter === f.id
                      ? 'bg-[#2c2416] text-[#f7f4ed] dark:bg-[#e8e6e3] dark:text-[#0f0f0f]'
                      : 'opacity-60 hover:opacity-100'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Featured: XISION */}
        {showFeatured && (
          <div {...reveal(120)}>
            <button
              onClick={() => setOpenId(featured.id)}
              className={`group w-full text-left mb-12 rounded-3xl border ${hairline} overflow-hidden grid lg:grid-cols-[1.25fr_1fr] bg-[#2c2416]/[0.015] dark:bg-[#e8e6e3]/[0.015] hover:border-[#2c2416]/25 dark:hover:border-[#e8e6e3]/25 transition-colors duration-500`}
            >
              <div className="p-8 sm:p-12 flex flex-col">
                <p className="text-[10px] tracking-widest uppercase opacity-60 mb-4">Featured · {featured.kicker}</p>
                <h3 className="text-[clamp(2rem,4vw,3rem)] font-extralight tracking-tight leading-none mb-2">{featured.title}</h3>
                <p className="text-[13px] tracking-wide opacity-70 mb-6">{featured.subtitle}</p>
                <p className="text-[14px] leading-relaxed font-light opacity-85 mb-8 max-w-[560px]">{featured.summary}</p>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mb-8">
                  {featured.pipeline.map((p, i) => (
                    <div key={i} className={`border-l ${hairline} pl-4`}>
                      <p className="text-[10px] tracking-widest opacity-40 mb-1">{String(i + 1).padStart(2, '0')}</p>
                      <p className="text-[13px] font-medium mb-0.5">{p.step}</p>
                      <p className="text-[12px] font-light opacity-70 leading-snug">{p.detail}</p>
                    </div>
                  ))}
                </div>
                <span className="mt-auto inline-flex items-center gap-2 text-[12px] tracking-widest uppercase group-hover:gap-3 transition-all duration-500">
                  Read case study <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </span>
              </div>
              <div className="relative min-h-[320px] bg-white flex items-center justify-center overflow-hidden">
                <div className="absolute inset-x-0 bottom-[18%] h-px bg-[#2c2416]/10" />
                <WalkSprite {...featured.media} className="relative h-[280px] sm:h-[340px] aspect-[220/409]" />
                <span className="absolute top-5 right-5 text-[9px] tracking-widest uppercase text-[#2c2416]/50">Walk cycle · Gaussian splats</span>
              </div>
            </button>
          </div>
        )}

        {/* Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
          {visible.map((p, i) => (
            <div key={p.id} {...reveal(200 + i * 80)}>
              <button onClick={() => setOpenId(p.id)} className="group w-full text-left">
                <div className={`relative aspect-[4/3] rounded-2xl overflow-hidden border ${hairline} bg-[#2c2416]/[0.02] dark:bg-[#e8e6e3]/[0.02] mb-5 group-hover:border-[#2c2416]/25 dark:group-hover:border-[#e8e6e3]/25 transition-colors duration-500`}>
                  <CardMedia project={p} />
                  <span className="absolute top-3 right-3 text-[9px] tracking-widest uppercase px-2.5 py-1 rounded-full bg-[#f7f4ed]/90 dark:bg-[#0f0f0f]/85 text-[#2c2416] dark:text-[#e8e6e3] backdrop-blur">
                    {p.category === 'vision' ? 'Vision' : 'Language'}
                  </span>
                </div>
                <p className="text-[10px] tracking-widest uppercase opacity-50 mb-2">{p.kicker}</p>
                <h3 className="text-[20px] font-medium tracking-tight mb-2 flex items-center gap-2">
                  {p.title}
                  <ArrowRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-70 group-hover:translate-x-0 transition-all duration-500" strokeWidth={1.5} />
                </h3>
                <p className="text-[13px] leading-relaxed font-light text-[#2c2416]/80 dark:text-[#e8e6e3]/75 mb-4">{p.summary}</p>
                <div className="flex flex-wrap gap-2">
                  {p.tags.map((t) => (
                    <span key={t} className="text-[9px] px-2.5 py-1 border border-[#2c2416]/15 dark:border-[#e8e6e3]/15 rounded-full tracking-widest font-light uppercase opacity-70">
                      {t}
                    </span>
                  ))}
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>

      {openProject && <ProjectModal project={openProject} onClose={close} onPrev={prev} onNext={next} />}
    </section>
  );
}
